import { useEffect, useState } from "react";
import { get, isEqual, noop } from "lodash";
import { t } from "@/i18n-js/instance";
import { defaultDisplayValue } from "./defaultDisplayValue";
import { filterByLabel } from "./filters";
import { useSearchOptions } from "./useSearchOptions";

export interface UseSearchableSelectProps<Option = unknown, Value = unknown> {
  displayValue?: (option: Option) => string | undefined;
  filterBy?: (
    options: Option[] | readonly Option[],
    search: string,
  ) => Option[];
  groupByValue?: (option: Option) => string | undefined;
  initialValue?: Value | "";
  initialValuePath?: string;
  isError?: boolean;
  onChange?: (value: Value | "") => void;
  onChangeValue?: (option: Option) => Value | "";
  onSearchChange?: (search: string) => void;
  options?: Option[] | readonly Option[];
  optionsClassName?: string;
  placeholder?: string;
  value?: Value;
}

const findOptionByValue = <Option = unknown, Value = unknown>(
  options: Option[] | readonly Option[],
  value: Value | undefined | "",
  valuePath: string,
) => options.find(option => get(option, valuePath) === value);

const defaultOnChangeValue = <Option = unknown, Value = unknown>(
  option: Option,
): Value | "" => {
  if (typeof option === "object" && option !== null && "value" in option) {
    return option.value as Value;
  }
  return "";
};

export const useSearchableSelect = <Option = unknown, Value = unknown>({
  options = [],
  onChange = noop,
  isError = false,
  filterBy = filterByLabel,
  placeholder = t("select_an_option"),
  displayValue = defaultDisplayValue,
  onChangeValue = defaultOnChangeValue,
  onSearchChange = noop,
  groupByValue,
  value,
  initialValue,
  initialValuePath = "value",
  optionsClassName,
}: UseSearchableSelectProps<Option, Value>) => {
  const hasInitialValue = Boolean(initialValue) && Boolean(initialValuePath);
  const initialOption =
    (hasInitialValue &&
      findOptionByValue(options, initialValue, initialValuePath)) ||
    null;

  const [currentOption, setCurrentOption] = useState<Option | null>(
    initialOption,
  );
  const clearValue = (): void => setCurrentOption(null);

  const {
    search,
    clearSearch,
    handleSearchChange,
    searchResults,
    hasNoResults,
    groupedSearchResults,
    hasGroups,
  } = useSearchOptions(options, filterBy, groupByValue, onSearchChange);

  const selectProps = {
    value: currentOption,
    onFilter: handleSearchChange,
    onClose: clearSearch,
    onChange: (option: Option): void => {
      if (isEqual(value, get(option, initialValuePath))) return;
      const extractedValue = onChangeValue(option);

      setCurrentOption(option);
      onChange(extractedValue);
    },
    placeholder,
    displayValue,
    isError,
    optionsClassName,
  };

  const dropdownProps = {
    ...selectProps,
    nullable: true as const,
    onClose: (): void => {
      clearValue();
      clearSearch();
    },
  };

  useEffect(() => {
    if (!initialValue) {
      clearValue();
    }
  }, [initialValue]);

  useEffect(() => {
    if (get(currentOption, initialValuePath) === value) return;

    const foundOption = findOptionByValue(options, value, initialValuePath);
    foundOption ? setCurrentOption(foundOption) : clearValue();
  }, [currentOption, initialValuePath, options, value]);

  return {
    search,
    selectProps,
    dropdownProps,
    searchResults,
    hasNoResults,
    groupedSearchResults,
    hasGroups,
  };
};
